import React, { useState } from 'react';

import ChatOutlinedIcon from '@mui/icons-material/ChatOutlined';
import CloseIcon from '@mui/icons-material/Close';
import Drawer from '@mui/material/Drawer';
import Fab from '@mui/material/Fab';
import IconButton from '@mui/material/IconButton';
import { type Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { Translate } from 'react-localize-redux';

import ErrorBoundary from '../../layouts/Portal/ErrorBoundary';
import { PortalFeatureFlags } from '../../services/featureFlags/portalFeatureFlags';
import { useFeatureFlagValue } from '../../services/featureFlags/useFeatureFlagValue';
import FlipdishAgentMFE from './FlipdishAgentMFE';

const drawerWidth = 420;

/** Matches FlipdishToolbar floating button size/position so we can clear it in non-prod. */
const TOOLBAR_FAB_SIZE = 56;
const TOOLBAR_FAB_SIZE_MOBILE = 48;
/** Gap between the agent FAB and the FlipdishToolbar FAB. */
const FAB_GAP = 12;

const isDev = !window.location?.host?.includes('portal.flipdish.com');

const useStyles = makeStyles((theme: Theme) => ({
  fab: {
    position: 'fixed',
    right: theme.spacing(3),
    bottom: theme.spacing(3),
    zIndex: theme.zIndex.drawer + 1,
  },
  // FlipdishToolbar sits bottom-right in non-prod; place the agent FAB to its left.
  fabBesideToolbar: {
    right: theme.spacing(2) + TOOLBAR_FAB_SIZE + FAB_GAP,
    bottom: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      right: theme.spacing(1.5) + TOOLBAR_FAB_SIZE_MOBILE + FAB_GAP,
      bottom: theme.spacing(1.5),
    },
  },
  drawerPaper: {
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: drawerWidth,
    },
  },
  panel: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    flexShrink: 0,
  },
  title: {
    flexGrow: 1,
    fontSize: 18,
    fontWeight: 600,
  },
  body: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
}));

/**
 * Global Flipdish Assistant: FAB + right side panel hosting the flipdish-agent MFE.
 * Mount once at Portal layout level (not a route). Gated by Harness
 * `frontend_portal_flipdishAssistant` (safe default: off).
 */
export const FlipdishAgentAssistant = () => {
  const classes = useStyles();
  const flagEnabled = useFeatureFlagValue(PortalFeatureFlags.flipdishAssistant, false);
  // Local Vite preview only (NODE_ENV=development). Jest uses NODE_ENV=test so the Harness
  // safe-off default still applies in CI.
  const isLocalDevPreview =
    process.env.NODE_ENV === 'development' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
  const isEnabled = flagEnabled || isLocalDevPreview;
  const [open, setOpen] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);

  const handleOpen = () => {
    setHasOpened(true);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  if (!isEnabled) {
    return null;
  }

  return (
    <>
      {!open && (
        <Fab
          color="primary"
          className={clsx(classes.fab, isDev && classes.fabBesideToolbar)}
          onClick={handleOpen}
          aria-label="Open Flipdish Assistant"
          data-fd="flipdish-agent-open"
        >
          <ChatOutlinedIcon />
        </Fab>
      )}
      <Drawer
        anchor="right"
        open={open}
        onClose={handleClose}
        // Keep the MFE mounted so an in-flight stream and chat state survive close/reopen.
        keepMounted
        disableEnforceFocus
        classes={{ paper: classes.drawerPaper }}
      >
        <div className={classes.panel} data-fd="flipdish-agent-drawer">
          <div className={classes.header}>
            <Typography variant="h6" className={classes.title}>
              <Translate id="Flipdish_Assistant" />
            </Typography>
            <IconButton
              aria-label="Close Flipdish Assistant"
              onClick={handleClose}
              size="small"
              data-fd="flipdish-agent-close"
            >
              <CloseIcon />
            </IconButton>
          </div>
          <div className={classes.body}>
            {hasOpened && (
              <ErrorBoundary identifier="flipdish-agent-widget" includeTowlie={false}>
                <FlipdishAgentMFE />
              </ErrorBoundary>
            )}
          </div>
        </div>
      </Drawer>
    </>
  );
};

export default FlipdishAgentAssistant;
